import Button from "components/foundation/Button";
import { Table } from "components/Common/Table/Table";
import React, { MouseEvent, useState } from "react";
import styled from "styled-components";
import { match } from "ts-pattern";
import dayjs from "dayjs";
import useMediaView from "features/common/hooks/useMediaView";
import copyToClipboard from "utils/copyClipboard";
import downloadPDF from "utils/downloadPdf";
import openPdfPreview from "utils/openPdfPreview";
import Toast from "components/foundation/Toast";
import { KebabIcon } from "assets/icons";
import BottomSheet from "components/foundation/BottomSheet/BottomSheet";
import { useDeleteDocumentMutation } from "features/document/hooks/mutations/useDeleteDocumentMutation";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import ButtonDropdown from "components/foundation/ButtonDropdown/ButtonDropdown";
import Modal from "components/foundation/Modal/Modal";
import ArchivesTagEditSelect from "components/document/edlt/ArchivesTagEditSelect";
import { DocumentClassDto, TagDto } from "apis/data-contracts";
import CheckBox from "components/foundation/CheckBox/CheckBox";

interface ArchiveTableRowProps {
  id: string;
  no: number;
  link?: string;
  title: string;
  documentType: string;
  regionType: string;
  businessType: string;
  createAt: string;
  author: string;
  authorUuid: string;
  documentFileUrl?: string;
  documentClassDto?: DocumentClassDto;
  freeTags?: TagDto[];
  onDeleteSuccess: () => void;
  onTagUpdateSuccess: () => void;
  isSelected?: boolean;
  onCheckBoxClick?: (id: string, documentFileUrl?: string) => void;
}

const ArchiveTableRow = ({
  id,
  no,
  title,
  documentType,
  regionType,
  businessType,
  createAt,
  documentFileUrl,
  authorUuid,
  author,
  onDeleteSuccess,
  freeTags,
  documentClassDto,
  onTagUpdateSuccess,
  isSelected = false,
  onCheckBoxClick,
}: ArchiveTableRowProps) => {
  const { data: userData } = useUserDetailQuery();

  // 대표이사, 관리자 계정만 삭제 가능 & 태그 수정까지 가능
  const hasAuthorityToDelete =
    userData?.adminType === "SUPERADMIN" || userData?.adminType === "ADMIN";

  // 작성자 본인은 태그 수정만 가능
  const hasAuthorityToTag = authorUuid === userData?.uuid;

  const isSmall = useMediaView("small");
  const [isEtcOpen, setIsEtcOpen] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openTagEditModal, setOpenTagEditModal] = useState(false);

  const handleOpenPdf = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.stopPropagation();
    void openPdfPreview(documentFileUrl, title);
  };

  const handleDownloadPdf = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && downloadPDF(documentFileUrl, formattedCreateAtDate);
  };

  const handleCopyLink = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    documentFileUrl && copyToClipboard(documentFileUrl);
    setIsToastOpen(true);
  };

  const formattedCreateAtDate = dayjs(createAt).format("YYYY-MM-DD");

  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    setIsDetailOpen((pre) => !pre);
  };

  const handlePdfBottomSheetOpenClick = (e: MouseEvent<HTMLOrSVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEtcOpen(true);
  };

  const deleteDocumentMutation = useDeleteDocumentMutation(id);
  const handleDeleteDocument = () => {
    deleteDocumentMutation.mutate(undefined, {
      onSuccess: () => {
        // BottomSheet 닫히고 나서 토스트 메시지 표시
        setTimeout(() => {
          setIsEtcOpen(false);
          onDeleteSuccess();
        }, 200);
      },
    });
  };

  const handleCheckBoxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCheckBoxClick?.(id, documentFileUrl);
  };

  return (
    <>
      <Table.Tr onClick={handleRowClick}>
        {match(isSmall)
          .with(true, () => (
            <>
              <Table.Td>{title}</Table.Td>
              <Table.Td>{documentType}</Table.Td>
              <Table.Td>{formattedCreateAtDate}</Table.Td>
              <Table.Td
                style={{
                  margin: "auto",
                }}
              >
                {documentFileUrl && (
                  <KebabIcon onClick={handlePdfBottomSheetOpenClick} />
                )}
                <Toast.Message
                  open={isToastOpen}
                  onOpenChange={(open) => setIsToastOpen(open)}
                >
                  링크가 복사되었습니다.
                </Toast.Message>
              </Table.Td>

              <BottomSheet
                title="PDF"
                isOpen={isEtcOpen}
                setOpen={setIsEtcOpen}
                hasAuthorityToDelete={hasAuthorityToDelete}
              >
                <div onClick={handleOpenPdf}>조회</div>
                <div onClick={handleDownloadPdf}>다운로드</div>
                <div onClick={handleCopyLink}>링크복사</div>

                {hasAuthorityToDelete && (
                  <div
                    onClick={() => {
                      setIsEtcOpen(false);
                      setOpenDeleteModal(true);
                    }}
                  >
                    삭제
                  </div>
                )}
              </BottomSheet>
            </>
          ))
          .otherwise(() => (
            <>
              <Table.Td
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CheckBox
                  onClick={handleCheckBoxClick}
                  checked={isSelected}
                  disabled={!documentFileUrl}
                />
              </Table.Td>
              <Table.Td>{no}</Table.Td>
              <Table.Td
                onClick={handleOpenPdf}
                style={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Table.Td>
              <Table.Td>{documentType}</Table.Td>
              <Table.Td>{author}</Table.Td>
              <Table.Td>{formattedCreateAtDate}</Table.Td>
              <Table.Td>
                {documentFileUrl && (
                  <ButtonWrapper>
                    <Button variant="tertiary" onClick={handleDownloadPdf}>
                      다운로드
                    </Button>
                    <Button variant="tertiary" onClick={handleCopyLink}>
                      링크복사
                    </Button>
                    <Toast.Message
                      open={isToastOpen}
                      onOpenChange={(open) => setIsToastOpen(open)}
                    >
                      링크가 복사되었습니다.
                    </Toast.Message>

                    <button
                      style={{
                        width: "4rem",
                      }}
                    >
                      {(hasAuthorityToDelete || hasAuthorityToTag) && (
                        <ButtonDropdown
                          list={[
                            ...(hasAuthorityToDelete
                              ? [
                                  {
                                    label: "삭제",
                                    width: "15rem",
                                    justifyContent: "left",
                                    onClick: () => setOpenDeleteModal(true),
                                  },
                                ]
                              : []),
                            {
                              label: "태그 수정",
                              width: "15rem",
                              justifyContent: "left",
                              onClick: () => {
                                setOpenTagEditModal(true);
                              },
                            },
                          ]}
                        >
                          <KebabIcon
                            style={{
                              width: "2.5rem",
                              height: "2.5rem",
                              margin: "auto",
                            }}
                          />
                        </ButtonDropdown>
                      )}
                    </button>
                  </ButtonWrapper>
                )}
              </Table.Td>
            </>
          ))}
        <Modal.Root open={openDeleteModal} onOpenChange={setOpenDeleteModal}>
          <Modal.Body
            onConfirm={handleDeleteDocument}
            onCancel={() => {
              setOpenDeleteModal(false);
            }}
            title="문서를 삭제하시겠습니까?"
          ></Modal.Body>
        </Modal.Root>
        <Modal.Root open={openTagEditModal} onOpenChange={setOpenTagEditModal}>
          <Modal.Layout>
            <Modal.Content>
              <ArchivesTagEditSelect
                uuid={id}
                freeTags={freeTags}
                documentClassDto={documentClassDto}
                onClose={() => {
                  setOpenTagEditModal(false);
                }}
                onTagUpdateSuccess={() => {
                  onTagUpdateSuccess();
                }}
              />
            </Modal.Content>
          </Modal.Layout>
        </Modal.Root>
      </Table.Tr>

      {isDetailOpen && isSmall && (
        <Table.Tr>
          <MobileDetailContent>
            {/* <MobileDetailListItem title="링크">{link}</MobileDetailListItem> */}
            <MobileDetailListItem title="지역구분">
              <div>{regionType}</div>
            </MobileDetailListItem>
            <MobileDetailListItem title="사업구분">
              {businessType}
            </MobileDetailListItem>
            <MobileDetailListItem title="주제분류">
              {documentType}
            </MobileDetailListItem>
          </MobileDetailContent>
        </Table.Tr>
      )}
    </>
  );
};

export default ArchiveTableRow;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;

const MobileDetailContent = styled.div`
  grid-column: span 4;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  background-color: ${({ theme }) => theme.color.background.default};
`;

interface MobileDetailListItemProps {
  title: string;
  children: React.ReactNode;
}

const MobileDetailListItem = ({
  title,
  children,
}: MobileDetailListItemProps) => {
  return (
    <MobileDetailListItemLayout>
      <h6>{title}</h6>
      {children}
    </MobileDetailListItemLayout>
  );
};

const MobileDetailListItemLayout = styled.li`
  display: flex;
  gap: 2rem;
  ${({ theme }) => theme.typography.caption1.regular};

  h6 {
    color: ${({ theme }) => theme.color.text.secondary};
    min-width: 6rem;
  }
`;
